import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getSupabaseClient } from "../services/supabaseClient";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

const formatRelativeTime = (value) => {
    if (!value) {
        return "Unknown";
    }
    const timestamp = new Date(value).getTime();
    if (Number.isNaN(timestamp)) {
        return "Unknown";
    }
    const diffMs = Date.now() - timestamp;
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) {
        return "Just now";
    }
    if (minutes < 60) {
        return `${minutes}m ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return `${hours}h ago`;
    }
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};

const formatDeviceInfo = (session) => {
    if (session.device_info) {
        return session.device_info;
    }

    const ua = session.user_agent || "";
    if (!ua) {
        return "Unknown device";
    }

    const browser =
        ua.includes("Edg/")
            ? "Edge"
            : ua.includes("Chrome/")
              ? "Chrome"
              : ua.includes("Firefox/")
                ? "Firefox"
                : ua.includes("Safari/")
                  ? "Safari"
                  : "Browser";
    const os =
        ua.includes("Windows")
            ? "Windows"
            : ua.includes("Mac OS")
              ? "macOS"
              : ua.includes("Android")
                ? "Android"
                : ua.includes("iPhone") || ua.includes("iPad")
                  ? "iOS"
                  : ua.includes("Linux")
                    ? "Linux"
                    : "OS";

    return `${browser} on ${os}`;
};

const formatDateTime = (value) => {
    if (!value) {
        return "Unknown";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return "Unknown";
    }
    return parsed.toLocaleString();
};

export default function SessionManagement() {
    const { currentSessionId } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pendingSession, setPendingSession] = useState(null);
    const [pendingRevokeAll, setPendingRevokeAll] = useState(false);

    const fetchSessions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const supabase = getSupabaseClient();
            const { data } = await supabase.auth.getSession();
            const token = data?.session?.access_token;
            if (!token) {
                throw new Error("Please sign in to view sessions.");
            }

            const response = await fetch(`${API_BASE_URL}/api/sessions`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "X-Session-ID": currentSessionId || "",
                },
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.error || "Unable to load sessions.");
            }

            const payload = await response.json().catch(() => ({}));
            setSessions(Array.isArray(payload?.sessions) ? payload.sessions : []);
        } catch (err) {
            setError(err?.message || "Unable to load sessions.");
        } finally {
            setLoading(false);
        }
    }, [currentSessionId]);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    const revokeSession = async (sessionId) => {
        if (!sessionId) {
            return;
        }
        try {
            const supabase = getSupabaseClient();
            const { data } = await supabase.auth.getSession();
            const token = data?.session?.access_token;
            if (!token) {
                throw new Error("Please sign in to revoke sessions.");
            }

            const response = await fetch(
                `${API_BASE_URL}/api/sessions/${sessionId}/revoke`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.error || "Unable to revoke session.");
            }

            await fetchSessions();
        } catch (err) {
            setError(err?.message || "Unable to revoke session.");
        }
    };

    const revokeAll = async () => {
        try {
            const supabase = getSupabaseClient();
            const { data } = await supabase.auth.getSession();
            const token = data?.session?.access_token;
            if (!token) {
                throw new Error("Please sign in to revoke sessions.");
            }

            const response = await fetch(`${API_BASE_URL}/api/sessions/revoke-all`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    currentSessionId: currentSessionId || null,
                }),
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.error || "Unable to revoke sessions.");
            }

            await fetchSessions();
        } catch (err) {
            setError(err?.message || "Unable to revoke sessions.");
        }
    };

    const hasOtherSessions = useMemo(() => {
        if (!currentSessionId) {
            return sessions.length > 1;
        }
        return sessions.some((session) => session.id !== currentSessionId);
    }, [currentSessionId, sessions]);

    return (
        <div className="profile-card">
            <div className="profile-card-header">
                <div>
                    <h2>Active sessions</h2>
                    <p className="text-muted">
                        Revoke sessions you do not recognize to protect your account.
                    </p>
                </div>
                <button
                    className="btn ghost"
                    type="button"
                    disabled={!hasOtherSessions}
                    onClick={() => setPendingRevokeAll(true)}
                >
                    Revoke all other sessions
                </button>
            </div>

            {loading && <p>Loading sessions...</p>}
            {error && <p className="error-message">{error}</p>}

            {!loading && sessions.length === 0 && (
                <p className="text-muted">No active sessions found.</p>
            )}

            {!loading && sessions.length > 0 && (
                <div className="session-table">
                    <div className="session-row session-header">
                        <span>Device</span>
                        <span>IP address</span>
                        <span>Last active</span>
                        <span>Created</span>
                        <span />
                    </div>
                    {sessions.map((session) => {
                        const isCurrent = session.id === currentSessionId;
                        return (
                            <div className="session-row" key={session.id}>
                                <div className="session-device">
                                    <span>{formatDeviceInfo(session)}</span>
                                    {isCurrent && (
                                        <span className="badge">Current session</span>
                                    )}
                                </div>
                                <span>{session.ip_address || "Unknown"}</span>
                                <span>{formatRelativeTime(session.last_active_at)}</span>
                                <span>{formatDateTime(session.created_at)}</span>
                                <div className="session-actions">
                                    {!isCurrent && (
                                        <button
                                            className="btn ghost"
                                            type="button"
                                            onClick={() => setPendingSession(session)}
                                        >
                                            Revoke
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {pendingSession && (
                <div className="modal-root">
                    <div className="modal-overlay" />
                    <div className="modal-card">
                        <div className="modal-header">
                            <h3>Revoke session</h3>
                        </div>
                        <p className="modal-body">
                            Revoke access for {formatDeviceInfo(pendingSession)}?
                        </p>
                        <div className="modal-actions">
                            <button
                                className="btn ghost"
                                type="button"
                                onClick={() => setPendingSession(null)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn primary"
                                type="button"
                                onClick={() => {
                                    revokeSession(pendingSession.id);
                                    setPendingSession(null);
                                }}
                            >
                                Revoke
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {pendingRevokeAll && (
                <div className="modal-root">
                    <div className="modal-overlay" />
                    <div className="modal-card">
                        <div className="modal-header">
                            <h3>Revoke all other sessions</h3>
                        </div>
                        <p className="modal-body">
                            This will sign out every other device except the current one.
                        </p>
                        <div className="modal-actions">
                            <button
                                className="btn ghost"
                                type="button"
                                onClick={() => setPendingRevokeAll(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn primary"
                                type="button"
                                onClick={() => {
                                    revokeAll();
                                    setPendingRevokeAll(false);
                                }}
                            >
                                Revoke sessions
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
